import React, { useState, useRef } from 'react'
import { User } from '@/types'

interface AvatarUploadProps {
  user: User
  onAvatarChange: (file: File) => void
  isLoading?: boolean
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
  user,
  onAvatarChange,
  isLoading = false
}) => {
  const [preview, setPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // 檢查檔案類型
      if (!file.type.startsWith('image/')) {
        alert('請選擇圖片檔案')
        return
      }

      // 檢查檔案大小 (限制 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('圖片檔案大小不能超過 5MB')
        return
      }

      // 創建預覽
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)

      // 呼叫父組件的處理函數
      onAvatarChange(file)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const getAvatarSrc = () => {
    if (preview) return preview
    if (user.avatar) {
      // 如果 avatar 是相對路徑，直接使用（會通過 nginx 代理）
      // 如果是完整 URL，也直接使用
      return user.avatar
    }
    return null
  }

  const getInitials = () => {
    return user.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <div
          className="w-24 h-24 rounded-full overflow-hidden bg-primary-100 dark:bg-primary-900 border-4 border-white dark:border-secondary-700 shadow-lg cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleClick}
        >
          {getAvatarSrc() ? (
            <img
              src={getAvatarSrc()!}
              alt={user.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-primary-600 dark:text-primary-400 text-xl font-semibold">
              {getInitials()}
            </div>
          )}
        </div>
        
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        
        <button
          type="button"
          onClick={handleClick}
          disabled={isLoading}
          className="absolute bottom-0 right-0 w-8 h-8 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={handleClick}
          disabled={isLoading}
          className="text-sm text-primary-600 hover:text-primary-700 disabled:text-primary-400 font-medium transition-colors"
        >
          {user.avatar || preview ? '更換頭像' : '上傳頭像'}
        </button>
        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
          支援 JPG、PNG 格式，檔案大小不超過 5MB
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}

export default AvatarUpload
